import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { X } from "lucide-react";
import type { AssetRef, BuilderApi, ImageLook, OwnerCommand, Workspace } from "../api/contract";
import { PhotoError, preparePhoto } from "../lib/photos";
import { Spinner } from "./Spinner";

export const MAX_PHOTOS = 12;

interface PhotoAddProps {
  api: BuilderApi;
  siteId: string;
  workspace: Workspace;
  files: File[];
  /** Called once the photos are in the owner's content, with their descriptions. */
  onAdded: (records: Extract<OwnerCommand, { kind: "media.add" }> ["records"]) => void | Promise<void>;
  onCancel: () => void;
  refresh: () => Promise<unknown>;
}

/**
 * Starts with the upload time, so photo ids sort by when they were added (the designer sees the
 * newest unplaced photos first); `position` keeps a batch in the order it was chosen.
 */
const recordId = (position: number) => `photo-${Date.now().toString(36)}-${String(position).padStart(2, "0")}${Math.random().toString(36).slice(2, 6)}`;

interface Uploaded { asset: AssetRef; preview: AssetRef | null }
/** One photo's background work: the upload (with its AI copy), then what the admin saw in it. */
interface PhotoJob { upload: Promise<Uploaded>; look: Promise<ImageLook | null> }

/**
 * Adds chosen photos to the owner's content: each needs a short description (it becomes the
 * image's alt text and tells the admin what it shows). Photos are prepared in the browser and
 * uploaded privately straight away; the admin then looks at each one and suggests a description,
 * which the owner can change. They are added together in one change; nothing appears on the site
 * until it is placed.
 */
export function PhotoAdd({ api, siteId, workspace, files, onAdded, onCancel, refresh }: PhotoAddProps) {
  const chosen = useMemo(() => files.slice(0, MAX_PHOTOS), [files]);
  // Created and revoked by the same effect, so a remount (StrictMode) never leaves revoked URLs behind.
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = chosen.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [chosen]);
  const [descriptions, setDescriptions] = useState<string[]>(() => chosen.map(() => ""));
  /** Photos whose description the owner has typed in; a suggestion never replaces their words. */
  const typed = useRef(new Set<number>());
  /** Settled looks by photo: a look, or null when the admin couldn't say. Missing means still looking. */
  const [looks, setLooks] = useState<Record<number, ImageLook | null>>({});
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [status, setStatus] = useState<{ phase: "idle" | "working" | "failed"; message?: string }>({ phase: "idle" });

  // Kept across StrictMode's remount, so each photo is uploaded and looked at once.
  const jobs = useRef(new Map<File, PhotoJob>());
  const jobFor = (file: File): PhotoJob => {
    let job = jobs.current.get(file);
    if (!job) {
      const upload = preparePhoto(file).then(async ({ photo, aiCopy }) => {
        const asset = await api.uploadPhoto(siteId, photo);
        const preview = aiCopy ? await api.uploadPhoto(siteId, aiCopy).catch(() => null) : null;
        return { asset, preview };
      });
      const look = upload.then(({ asset, preview }) => api.describePhoto(siteId, preview ?? asset)).catch(() => null);
      job = { upload, look };
      jobs.current.set(file, job);
    }
    return job;
  };
  useEffect(() => {
    let live = true;
    chosen.forEach((file, index) => {
      void jobFor(file).look.then((look) => {
        if (!live) return;
        setLooks((current) => ({ ...current, [index]: look }));
        // Only fills an empty field the owner hasn't written in.
        if (look && !typed.current.has(index)) setDescriptions((current) => current.map((value, at) => at === index && !value.trim() ? look.description : value));
      });
    });
    return () => { live = false; };
    // jobFor reads only props that are fixed for this form (it is keyed by its files); jobs are per file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen]);

  const kept = chosen.map((file, index) => ({ file, index })).filter(({ index }) => !removed.has(index));
  const ready = kept.length > 0 && kept.every(({ index }) => descriptions[index]!.trim());

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || status.phase === "working") return;
    setStatus({ phase: "working", message: "Preparing your photos…" });
    try {
      const records = [];
      for (const [position, { file, index }] of kept.entries()) {
        setStatus({ phase: "working", message: `Uploading ${position + 1} of ${kept.length}…` });
        // Usually already uploaded in the background; if that failed, try once more now.
        // An SVG is only accepted with its AI copy, so the retry uploads that too.
        const uploaded = await jobFor(file).upload.catch(async () => {
          const { photo, aiCopy } = await preparePhoto(file);
          const asset = await api.uploadPhoto(siteId, photo);
          return { asset, preview: aiCopy && asset.mimeType === "image/svg+xml" ? await api.uploadPhoto(siteId, aiCopy) : null };
        });
        const look = looks[index];
        records.push({ id: recordId(position), alt: descriptions[index]!.trim(), asset: uploaded.asset, ...(uploaded.preview ? { preview: uploaded.preview } : {}), ...(look ? { look } : {}) });
      }
      setStatus({ phase: "working", message: "Adding them to your Content…" });
      const action = await api.submitAction(siteId, { actionId: `photos-${Date.now().toString(36)}`, baseDraftId: workspace.site.draftId, command: { kind: "media.add", records } });
      if (!["queued", "running", "succeeded"].includes(action.state)) throw new Error("Not added");
      await refresh();
      await onAdded(records);
    } catch (caught) {
      setStatus({ phase: "failed", message: caught instanceof PhotoError ? caught.message : "Your photos couldn’t be added. Nothing was changed. Try again." });
    }
  };

  const working = status.phase === "working";
  return (
    <form className="bw-photo-add" onSubmit={(event) => void submit(event)} aria-labelledby="bw-photo-add-title">
      <h3 id="bw-photo-add-title">Add {kept.length === 1 ? "a photo" : `${kept.length} photos`}</h3>
      <p className="bw-item-note">Say what each one shows. Stax suggests a description where it can, so check it’s right. It helps place the photo, and helps visitors who can’t see it.</p>
      {files.length > MAX_PHOTOS && <p className="bw-item-note">Only the first {MAX_PHOTOS} are added at a time.</p>}
      <ul>
        {chosen.map((file, index) => {
          if (removed.has(index)) return null;
          const describing = !(index in looks) && !descriptions[index]!.trim();
          return (
            <li key={`${file.name}-${index}`}>
              {previews[index] ? <img src={previews[index]} alt="" /> : <span className="bw-thumb" />}
              <label>
                <span className="bw-sr">What this photo shows</span>
                <input
                  value={descriptions[index]}
                  placeholder="e.g. Two-tier wedding cake with roses"
                  maxLength={200}
                  disabled={working}
                  aria-busy={describing}
                  onChange={(event) => {
                    typed.current.add(index);
                    setDescriptions(descriptions.map((value, at) => at === index ? event.target.value : value));
                  }}
                />
                {describing && <span className="bw-photo-describing" role="status"><Spinner /> Describing…</span>}
              </label>
              <button type="button" className="bw-btn bw-btn--icon" aria-label={`Don’t add ${file.name}`} disabled={working} onClick={() => setRemoved(new Set([...removed, index]))}>
                <X size={18} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {status.message && (
        <p className="bw-item-note" role={status.phase === "failed" ? "alert" : "status"}>
          {status.phase === "working" && <Spinner />} {status.message}
        </p>
      )}
      <div className="bw-actions">
        <button type="submit" className="bw-btn bw-btn--primary" disabled={!ready || working}>
          {working && <Spinner />}
          {working ? "Adding…" : kept.length === 1 ? "Add photo" : `Add ${kept.length} photos`}
        </button>
        <button type="button" className="bw-btn bw-btn--quiet" onClick={onCancel} disabled={working}>Cancel</button>
      </div>
    </form>
  );
}
